import Cookies from "js-cookie";

export const JWT_COOKIE = "esiq-nada-jwt";

export const getToken = (): string | undefined => Cookies.get(JWT_COOKIE);

/**
 * Decode the `expires_at` claim from the JWT payload without verifying the
 * signature. The backend stores it as a UTC timestamp string formatted
 * "YYYY-MM-DD HH:MM:SS" (see workforce/auth/token.py:create_access_token).
 * Returns null if the token can't be parsed or has no expiry claim.
 */
export const getTokenExpiry = (token: string): Date | null => {
    try {
        const payloadSegment = token.split(".")[1];
        if (!payloadSegment) return null;

        const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
        const json = decodeURIComponent(
            atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );
        const payload = JSON.parse(json);
        if (!payload?.expires_at) return null;

        // Stored in UTC — normalise to an ISO string the Date parser treats as UTC.
        const date = new Date(payload.expires_at.replace(" ", "T") + "Z");
        return isNaN(date.getTime()) ? null : date;
    } catch {
        return null;
    }
};

/**
 * Whether the token's `expires_at` is in the past. If the expiry can't be
 * determined we return false and let the server be the source of truth (a 401
 * will then trigger re-auth via handleSessionExpiry).
 */
export const isTokenExpired = (token: string): boolean => {
    const expiry = getTokenExpiry(token);
    if (!expiry) return false;
    return Date.now() >= expiry.getTime();
};

// Guard so the many parallel dashboard requests that all 401 at once only kick
// off a single reload, and so the watcher/interceptor never stack reloads.
let handlingExpiry = false;

/**
 * Clear the expired session and reload. The reload re-runs AuthWrapper, whose
 * /user_info check now 401s and renders the LoginPage on the same URL — the same
 * recovery a manual refresh gave, but triggered automatically instead of leaving
 * the page in a silent "No data available" state (ES-1030).
 */
export const handleSessionExpiry = (): void => {
    if (handlingExpiry || typeof window === "undefined") return;
    handlingExpiry = true;
    Cookies.remove(JWT_COOKIE);
    window.location.reload();
};
